import React, { useEffect, useRef, useState } from "react";
import { Plus, Trash2, GripVertical, Upload, Image as ImageIcon, Edit3, Check, X, Search, AlertTriangle } from "lucide-react";
import api, { friendlyMessage } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { SortableList } from "../../components/SortableList";
import { toast } from "sonner";
import EmptyState, { LoadingState } from "../../components/EmptyState";
import ImageCropDialog from "../../components/ImageCropDialog";

export default function AdminCategories() {
  const [cats, setCats] = useState([]);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const blank = { name: "", icon: "", image_url: "", display_order: 0, parent_id: "" };
  const [form, setForm] = useState(blank);
  const [q, setQ] = useState("");
  const fileInputRef = useRef(null);
  const [cropSrc, setCropSrc] = useState(null);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const load = async () => {
    setLoadError(null);
    try {
      const { data } = await api.get("/api/admin/categories");
      setCats(data.categories);
    } catch (e) {
      setLoadError(friendlyMessage(e));
      toast.error(friendlyMessage(e));
    }
  };
  useEffect(() => { load().finally(() => setLoading(false)); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      if (editing) {
        await api.patch(`/api/admin/categories/${editing}`, form);
        toast.success("Category updated");
      } else {
        await api.post("/api/admin/categories", form);
        toast.success("Category added");
      }
      setOpen(false); setEditing(null); setForm(blank);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this category? Products linked to it will be kept but unlinked.")) return;
    try {
      const { data } = await api.delete(`/api/admin/categories/${id}`);
      toast.success(data.soft_deleted ? `Disabled (still linked to ${data.in_use} products)` : "Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const startEdit = (c) => {
    setEditing(c.id);
    setForm({
      name: c.name, icon: c.icon || "", image_url: c.image_url || "",
      display_order: c.display_order || 0, parent_id: c.parent_id || "",
    });
    setOpen(true);
  };

  // Categories eligible to be picked as a parent: top-level only (no
  // nesting a subcategory under another subcategory), excluding whichever
  // category is currently being edited.
  const parentOptions = cats.filter((c) => !c.parent_id && c.id !== editing);
  const editingCat = editing ? cats.find((c) => c.id === editing) : null;
  // A category that already has subcategories of its own can never become
  // a subcategory itself (backend rejects this) — hide the parent picker
  // for it rather than let the admin hit a 400 after filling out the form.
  const parentPickerDisabled = !!(editingCat?.subcategory_count > 0);

  const reorder = async (next) => {
    setCats(next);  // optimistic update
    try {
      await api.post("/api/admin/categories/reorder", { ids: next.map((c) => c.id) });
      toast.success("Order saved");
    } catch {
      toast.error("Reorder failed");
      load();
    }
  };

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("folder", "categories");
    setBusy(true);
    try {
      const { data } = await api.post("/api/admin/upload/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  // File picked → crop to square first (category cards render 1:1
  // everywhere, web and the Capacitor mobile app alike) → then upload.
  const pickImage = (file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) { toast.error("Pick an image file"); return; }
    setCropSrc(URL.createObjectURL(file));
  };
  const handleCropped = async (blob) => {
    URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
    await upload(new File([blob], "category.jpg", { type: "image/jpeg" }));
  };
  const cancelCrop = () => {
    URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  if (loading) return <LoadingState fullHeight />;
  if (loadError) {
    return (
      <EmptyState icon={AlertTriangle} title="Couldn't load categories" description={loadError}
        action={<button onClick={() => { setLoading(true); load().finally(() => setLoading(false)); }} className="h-10 px-4 rounded-2xl bg-brand-sky text-white text-sm font-bold">Retry</button>}
        fullHeight />
    );
  }

  return (
    <div className="space-y-6 page-enter" data-testid="admin-categories-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider font-bold text-neutral-500">Catalog</div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Categories ({cats.length})</h1>
          <p className="text-sm text-neutral-600 mt-1">Group products by category. Each can have its own banner image.</p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <Input placeholder="Search categories" className="pl-10 h-11 rounded-2xl w-full sm:w-64" value={q} onChange={(e) => setQ(e.target.value)} data-testid="categories-search-input"/>
          </div>
          <Button onClick={() => { setEditing(null); setForm({ ...blank, display_order: cats.length + 1 }); setOpen(true); }}
                  className="h-11 rounded-2xl bg-brand-sky hover:bg-blue-700 font-bold w-full sm:w-auto"
                  data-testid="add-category-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Category
          </Button>
        </div>
      </div>

      {cats.length === 0 ? (
        <div className="rounded-3xl bg-white border border-dashed border-neutral-300" data-testid="empty-categories">
          <EmptyState
            icon={ImageIcon}
            size="lg"
            title="No categories yet"
            description="Add your first product category to start building your catalog."
          />
        </div>
      ) : (
        <SortableList
          items={cats.filter((c) => !q.trim() || (c.name || "").toLowerCase().includes(q.toLowerCase()) || (c.slug || "").toLowerCase().includes(q.toLowerCase()))}
          onReorder={reorder}
          keyOf={(c) => c.id}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
          testIdPrefix="cat-sort"
          renderItem={(c, handle) => (
            <div className={`bg-white rounded-3xl border ${c.is_active ? "border-neutral-200" : "border-dashed border-neutral-300 opacity-60"} overflow-hidden`} data-testid={`category-${c.slug}`}>
              <div className="aspect-[16/10] bg-brand-cream relative">
                {c.image_url ? (
                  <img src={c.image_url} alt={c.name} className="w-full h-full object-cover"/>
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-neutral-400 text-4xl font-heading font-extrabold">
                    {c.name?.[0]?.toUpperCase()}
                  </div>
                )}
                <span className="absolute top-3 left-3 px-2 py-0.5 rounded-full bg-white/90 text-[10px] font-bold uppercase tracking-wider text-neutral-600">#{c.display_order}</span>
                {!c.is_active && <span className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-brand-clay/90 text-[10px] font-bold uppercase tracking-wider text-white">disabled</span>}
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    {handle}
                    <div className="min-w-0">
                      <div className="font-heading font-bold truncate">{c.name}</div>
                      <div className="text-xs text-neutral-500 font-mono mt-0.5">{c.slug}</div>
                      {c.parent_id && (
                        <div className="text-[10px] text-brand-sky font-bold mt-0.5 truncate" data-testid={`subcategory-of-${c.slug}`}>
                          ↳ Subcategory of {cats.find((p) => p.id === c.parent_id)?.name || "…"}
                        </div>
                      )}
                      {!c.parent_id && c.subcategory_count > 0 && (
                        <div className="text-[10px] text-neutral-500 font-bold mt-0.5" data-testid={`subcategory-count-${c.slug}`}>
                          {c.subcategory_count} subcategor{c.subcategory_count === 1 ? "y" : "ies"}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono font-extrabold text-brand-sky">{c.product_count || 0}</div>
                    <div className="text-[10px] uppercase tracking-wider text-neutral-500">products</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <button onClick={() => startEdit(c)} className="text-sm text-brand-sky font-bold inline-flex items-center gap-1" data-testid={`edit-${c.slug}`}>
                    <Edit3 className="w-3.5 h-3.5"/> Edit
                  </button>
                  <button onClick={() => del(c.id)} className="text-sm text-brand-clay font-bold inline-flex items-center gap-1 ml-auto" data-testid={`delete-${c.slug}`}>
                    <Trash2 className="w-3.5 h-3.5"/> Delete
                  </button>
                </div>
              </div>
            </div>
          )}
        />
      )}

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(blank); } }}>
        <DialogContent className="max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{editing ? "Edit category" : "New category"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-neutral-500">Name *</label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     placeholder="e.g. Milk & Dairy" className="h-12 rounded-2xl mt-1" data-testid="cat-name"/>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-wider font-bold text-neutral-500">Icon (lucide name)</label>
                <Input value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })}
                       placeholder="Milk" className="h-12 rounded-2xl mt-1" data-testid="cat-icon"/>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-bold text-neutral-500">Order</label>
                <Input type="number" value={form.display_order}
                       onChange={(e) => setForm({ ...form, display_order: parseInt(e.target.value) || 0 })}
                       className="h-12 rounded-2xl mt-1" data-testid="cat-order"/>
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-neutral-500">Parent category (optional)</label>
              {parentPickerDisabled ? (
                <div className="h-12 rounded-2xl border border-neutral-200 bg-neutral-50 mt-1 flex items-center px-4 text-sm text-neutral-500" data-testid="cat-parent-disabled-note">
                  This category has its own subcategories — it can't also be a subcategory.
                </div>
              ) : (
                <select value={form.parent_id} onChange={(e) => setForm({ ...form, parent_id: e.target.value })}
                        className="w-full h-12 rounded-2xl border border-neutral-200 px-4 text-sm bg-white mt-1" data-testid="cat-parent-select">
                  <option value="">None — top-level category</option>
                  {parentOptions.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              )}
              <p className="text-xs text-neutral-500 mt-1">
                Pick a parent to make this a subcategory. Only one level of nesting is supported.
              </p>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-neutral-500">Banner image</label>
              <div className="flex items-center gap-3 mt-1">
                <div className="w-16 h-16 rounded-2xl bg-brand-cream border border-neutral-200 flex items-center justify-center overflow-hidden shrink-0">
                  {form.image_url ? (
                    <img src={form.image_url} alt="" className="w-full h-full object-cover" data-testid="cat-image-preview"/>
                  ) : <ImageIcon className="w-6 h-6 text-neutral-400"/>}
                </div>
                <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={(e) => pickImage(e.target.files?.[0])} data-testid="cat-file-input"/>
                <Button onClick={() => fileInputRef.current?.click()} disabled={busy} variant="outline"
                        className="h-12 rounded-2xl border-2 border-brand-ink font-bold flex-1"
                        data-testid="cat-upload-btn">
                  <Upload className="w-4 h-4 mr-2"/> {form.image_url ? "Replace" : "Upload"} banner
                </Button>
              </div>
            </div>
            <Button onClick={save} disabled={busy} className="h-12 rounded-2xl bg-brand-sky hover:bg-blue-700 font-bold w-full" data-testid="save-category-btn">
              <Check className="w-4 h-4 mr-2"/> {editing ? "Save changes" : "Create category"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ImageCropDialog
        open={!!cropSrc}
        imageSrc={cropSrc}
        aspect={1}
        title="Crop category image"
        onCancel={cancelCrop}
        onCropped={handleCropped}
      />
    </div>
  );
}
